/**
 * The sidebar: a brand header, grouped navigation, and a footer.
 *
 * Navigation was a row of cards in the content column, so it scrolled away and
 * competed with the page's own heading. A nav item can carry a status dot,
 * which is how a surface says it needs attention without a number.
 *
 * @module components/ui/Sidebar
 */

import React, { type ReactNode } from "react";

import { NavLink, useLocation } from "react-router";

import { Icon } from "./Icon";

export interface SidebarProps {
  /** Brand mark and product name. */
  header: ReactNode;
  /** Context above the navigation: which programme is open. */
  context?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  "data-testid"?: string;
}

/** The 260px column, scrolling independently of the content. */
export function Sidebar({
  header,
  context = undefined,
  children,
  footer = undefined,
  "data-testid": testId,
}: SidebarProps): React.ReactElement {
  return (
    <aside
      className="flex h-[100dvh] w-[260px] flex-col overflow-y-auto border-r border-base-300 bg-base-200"
      data-testid={testId}
    >
      <div className="border-b border-base-300 p-4">{header}</div>
      {context && <div className="p-4 pb-0">{context}</div>}
      <nav aria-label="Main" className="flex flex-1 flex-col gap-4 p-4">
        {children}
      </nav>
      {footer && <div className="border-t border-base-300 p-4">{footer}</div>}
    </aside>
  );
}

export interface SidebarSectionProps {
  /** Omit for the primary group, which needs no label above it. */
  label?: string;
  children: ReactNode;
}

/** A labelled group of destinations. */
export function SidebarSection({
  label = undefined,
  children,
}: SidebarSectionProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <p className="px-3 pt-2 text-xs font-bold tracking-[0.09em] text-muted uppercase">
          {label}
        </p>
      )}
      {children}
    </div>
  );
}

export const NAV_STATUS_TONES = ["success", "warning", "error", "neutral"] as const;
export type NavStatusTone = (typeof NAV_STATUS_TONES)[number];

export interface SidebarNavItemProps {
  to: string;
  label: string;
  icon?: string;
  /** Matches only the exact path, for an index route. */
  end?: boolean;
  /**
   * Paths under `to` that belong to a different item, so this one is not
   * active on them. `end` cannot express it: it makes the item match only its
   * own path, which then leaves its real children with nothing highlighted.
   */
  notUnder?: readonly string[];
  /** A dot beside the label. Needs `statusLabel` to mean anything. */
  status?: NavStatusTone;
  /** What the dot says, e.g. "2 actions required". */
  statusLabel?: string;
  "data-testid"?: string;
}

const STATUS_CLASS: Record<NavStatusTone, string> = {
  success: "bg-success-strong",
  warning: "bg-warning-strong",
  error: "bg-error-strong",
  neutral: "bg-border-strong",
};

/**
 * One destination.
 *
 * The status dot is drawn from a `-strong` token, since a bare 8px mark on the
 * sidebar surface has nothing but its colour to be seen by, and it carries its
 * meaning as text for anyone who cannot use the colour.
 */
export function SidebarNavItem({
  to,
  label,
  icon = undefined,
  end = false,
  notUnder = undefined,
  status = undefined,
  statusLabel = undefined,
  "data-testid": testId,
}: SidebarNavItemProps): React.ReactElement {
  const { pathname } = useLocation();
  const claimedByAnother = (notUnder ?? []).some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
  return (
    <NavLink
      to={to}
      end={end}
      // NavLink names itself the current page whenever it matches; when a
      // deeper item owns the path, say so explicitly rather than leaving two
      // links claiming it.
      aria-current={claimedByAnother ? "false" : "page"}
      data-testid={testId}
      className={({ isActive }) =>
        [
          "flex w-full items-center gap-3 rounded-box border px-3 py-2 text-sm font-medium",
          isActive && !claimedByAnother
            ? "border-base-300 bg-base-100 font-semibold text-base-content"
            : "border-transparent text-muted hover:bg-surface-hover hover:text-base-content",
        ].join(" ")
      }
    >
      {/* A slot of one width and one glyph size, so labels share a left edge
          whatever each glyph's own advance is. */}
      {icon && (
        <span className="inline-flex size-5 shrink-0 items-center justify-center text-[18px] leading-none">
          <Icon name={icon} aria-hidden />
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {status && (
        <>
          <span
            aria-hidden="true"
            className={`size-2 shrink-0 rounded-full ${STATUS_CLASS[status]}`}
          />
          <span className="sr-only">{statusLabel ?? status}</span>
        </>
      )}
    </NavLink>
  );
}
